import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useUserProfile } from '@/hooks/useUserProfile';
import { useCanEditUsername } from '@/hooks/useCanEditUsername';
import { documentTypeLabel } from '@/lib/documentTypes';
import { updateUsername } from '@/lib/api/core';
import { ApiError } from '@/lib/api';
import { Button, Card, CardBody, CardTitle, Input, Modal, toast } from '@/components/ui';

const SUPPORT_EMAIL = 'soporte@firstclass.com';
const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

/**
 * AccountProfile — `/account/profile`. ROUND11 (admin R25): identidad
 * v2 — email + cédula read-only siempre; username editable UNA sola vez
 * (gated por RPC `fn_username_can_edit()`), después read-only con tooltip.
 *
 * - Errores del trigger `enforce_identity_immutability` se parsean por
 *   código y se toastean en español (`identityErrors.ts`).
 * - El RPC se invalida después de un update exitoso → el botón "Editar"
 *   desaparece automáticamente.
 */
export function AccountProfile(): JSX.Element {
  const { data: profile, isLoading, isError } = useUserProfile();

  return (
    <div className="flex flex-col gap-6">
      <Card variant="default" padded>
        <CardTitle>Datos personales</CardTitle>
        <CardBody>
          {isLoading ? (
            <div aria-busy="true" className="flex flex-col gap-2">
              <div className="h-4 w-48 rounded bg-neutral-100" />
              <div className="h-4 w-64 rounded bg-neutral-100" />
            </div>
          ) : isError || !profile ? (
            <p className="text-sm text-fc-muted" role="alert">
              No pudimos cargar tus datos en este momento. Probá recargar la página.
            </p>
          ) : (
            <>
              <dl className="grid grid-cols-1 gap-y-2 text-sm md:grid-cols-3">
                <dt className="text-fc-muted">Nombre</dt>
                <dd className="text-fc-ink md:col-span-2">{profile.fullName}</dd>
                <dt className="text-fc-muted">Email</dt>
                <dd className="text-fc-ink md:col-span-2">{profile.email}</dd>
                <dt className="text-fc-muted">Teléfono</dt>
                <dd className="text-fc-ink md:col-span-2">{profile.phone ?? 'Sin registrar'}</dd>
              </dl>
              <p className="mt-4 text-xs text-fc-muted">
                Tu correo es de solo lectura. Para cambiarlo, contactá a soporte (
                <a className="underline hover:text-fc-ink" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
                ).
              </p>
            </>
          )}
        </CardBody>
      </Card>

      {profile && (
        <>
          <DocumentCard
            documentType={profile.documentType}
            documentNumber={profile.documentNumber}
          />
          <UsernameCard username={profile.username} />
        </>
      )}

      <PasswordChangeCard />
    </div>
  );
}

function DocumentCard({
  documentType,
  documentNumber,
}: {
  documentType: string | null;
  documentNumber: string | null;
}): JSX.Element {
  return (
    <Card variant="default" padded>
      <CardTitle>Documento de identidad</CardTitle>
      <CardBody>
        <dl className="grid grid-cols-1 gap-y-2 text-sm md:grid-cols-3">
          <dt className="text-fc-muted">Tipo de documento</dt>
          <dd className="text-fc-ink md:col-span-2">{documentTypeLabel(documentType)}</dd>
          <dt className="text-fc-muted">Número de documento</dt>
          <dd className="text-fc-ink md:col-span-2">{documentNumber ?? 'No registrado'}</dd>
        </dl>
        <p className="mt-4 text-xs text-fc-muted">
          Tu cédula es de solo lectura. Para corregirla, contactá a soporte (
          <a className="underline hover:text-fc-ink" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
          ).
        </p>
      </CardBody>
    </Card>
  );
}

/**
 * UsernameCard — gate de edición via `fn_username_can_edit()`.
 *
 * Estados:
 *  1. RPC=true, !editing → muestra @username + botón "Editar".
 *  2. RPC=true, editing → input + warning + Guardar/Cancelar; Guardar → modal.
 *  3. RPC=false → @username deshabilitado + tooltip "Ya cambiaste tu username una vez".
 */
function UsernameCard({ username }: { username: string | null }): JSX.Element {
  const canEdit = useCanEditUsername();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [newUsername, setNewUsername] = useState(username ?? '');
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const mutation = useMutation({
    mutationFn: (next: string) => updateUsername(next),
    onSuccess: () => {
      // El cambio es de una sola vez: refrescamos perfil + gate para que el
      // botón "Editar" desaparezca y se muestre el nuevo @username.
      void queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['can-edit-username'] });
      setShowModal(false);
      setEditing(false);
      toast.success({
        title: 'Nombre de usuario actualizado',
        description: `Ahora sos @${newUsername}.`,
      });
    },
    onError: (e: unknown) => {
      setShowModal(false);
      const message =
        e instanceof ApiError
          ? e.message
          : 'No pudimos actualizar tu nombre de usuario. Intentá de nuevo.';
      toast.error({ title: 'No se pudo cambiar', description: message });
    },
  });

  const pending = mutation.isPending;
  const isEditable = canEdit.data === true;

  function startEdit(): void {
    setNewUsername(username ?? '');
    setError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setEditing(false);
    setError(null);
    setNewUsername(username ?? '');
  }

  function clickSave(): void {
    setError(null);
    if (!USERNAME_RE.test(newUsername)) {
      setError('3-20 caracteres: minúsculas, números o guión bajo (_).');
      return;
    }
    if (newUsername === username) {
      setError('Elegí un nombre de usuario distinto al actual.');
      return;
    }
    setShowModal(true);
  }

  async function confirmChange(): Promise<void> {
    // Cambio de una sola vez vía `PATCH /v1/profile/username` (cierre de
    // GAP-03). El backend valida la regla de inmutabilidad; en éxito
    // invalidamos perfil + gate para reflejar el nuevo @username read-only.
    if (!USERNAME_RE.test(newUsername) || newUsername === username) {
      setShowModal(false);
      return;
    }
    await mutation.mutateAsync(newUsername).catch(() => {
      // El onError del hook ya muestra el toast; no re-lanzamos.
    });
  }

  return (
    <Card variant="default" padded>
      <CardTitle>Nombre de usuario</CardTitle>
      <CardBody>
        {!editing ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-base font-medium text-fc-ink">
                {username ? `@${username}` : 'No registrado'}
              </p>
              {!isEditable && username && (
                <p
                  className="mt-1 inline-flex items-center gap-1 text-xs text-fc-muted"
                  title="Ya cambiaste tu username una vez"
                >
                  <LockIcon />
                  <span>Bloqueado — ya cambiaste tu nombre de usuario una vez.</span>
                </p>
              )}
            </div>
            {canEdit.isLoading ? (
              <span className="text-xs text-fc-muted">Verificando…</span>
            ) : isEditable ? (
              <Button variant="secondary" size="sm" onClick={startEdit}>
                Editar
              </Button>
            ) : (
              <span
                aria-label="Ya cambiaste tu username una vez"
                title="Ya cambiaste tu username una vez"
                className="inline-flex items-center gap-1 rounded-card border border-neutral-200 bg-neutral-100 px-3 py-1.5 text-xs text-fc-muted"
              >
                <LockIcon />
                Solo lectura
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div
              role="alert"
              className="rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-fc-ink"
            >
              ⚠️ Esta es tu <strong>única oportunidad</strong> para cambiar tu nombre de usuario.
              Tras guardar no podrás modificarlo de nuevo, ni siquiera el equipo de soporte.
            </div>
            <Input
              label="Nuevo nombre de usuario"
              type="text"
              autoComplete="username"
              required
              hint="Minúsculas, números o guión bajo (_), 3-20 caracteres."
              error={error ?? undefined}
              value={newUsername}
              onChange={(e) => {
                setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                if (error) setError(null);
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button onClick={clickSave} disabled={pending}>
                Guardar cambios
              </Button>
              <Button variant="ghost" onClick={cancelEdit} disabled={pending}>
                Cancelar
              </Button>
            </div>
          </div>
        )}
      </CardBody>

      <Modal
        open={showModal}
        onOpenChange={setShowModal}
        title="Confirmar cambio de nombre de usuario"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)} disabled={pending}>
              Cancelar
            </Button>
            <Button onClick={() => void confirmChange()} disabled={pending}>
              {pending ? 'Guardando…' : 'Sí, cambiar definitivamente'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fc-ink">
          Vas a cambiar tu username de <strong>@{username}</strong> a{' '}
          <strong>@{newUsername}</strong>.
        </p>
        <p className="mt-3 rounded-card border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-fc-ink">
          Esta es tu <strong>ÚNICA oportunidad</strong>. Tras este cambio no podrás modificarlo
          nunca más (ni siquiera el equipo de soporte).
        </p>
      </Modal>
    </Card>
  );
}

function LockIcon(): JSX.Element {
  return (
    <svg
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function PasswordChangeCard(): JSX.Element {
  // GAP-08 (ver endpoint_gaps.md): api-core no expone un cambio de contraseña
  // para sesión activa; sí el flujo de reset por email. Dirigimos a ese flujo
  // en vez de intentar una operación no soportada.
  return (
    <Card variant="default" padded>
      <CardTitle>Cambiar contraseña</CardTitle>
      <p className="mt-2 text-sm text-fc-muted">
        Para cambiar tu contraseña te enviamos un enlace seguro a tu correo.
      </p>
      <div className="mt-4">
        <Button as={Link} to="/forgot-password" variant="secondary">
          Enviarme un enlace de cambio
        </Button>
      </div>
    </Card>
  );
}
